import { Head, Link, router, usePage } from "@inertiajs/react"
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import DownloadIcon from "@mui/icons-material/Download"
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import EntryRow, { type IExportEntryRow } from "./EntryRow"
import OrderPickerDialog, { type IOrderOption } from "@/components/OrderPickerDialog"

interface IExport {
    id?: number
    name: string
    client_name: string
    update_date: string
    export_entries: IExportEntryRow[]
}

const EMPTY_FORM = (): IExport => ({
    name: "",
    client_name: "",
    update_date: dayjs().format("YYYY-MM-DD"),
    export_entries: [],
})

const EMPTY_ENTRY = (): IExportEntryRow => ({
    order_id: null,
    op_date: null,
    reference_number: null,
    project_name: "",
    paper_color: 0,
    printing_inks_amount: 0,
    kilograms: 0,
    art_request: 0,
    plate_request: 0,
    plate_approval: 0,
    export_number: "",
    exit_date: null,
    urgent: false,
    order: null,
})

type Props = {
    is_edit: boolean
    export: IExport | null
    excluded_order_ids: number[]
    back_params: Record<string, string | null>
    errors?: Record<string, string[]>
}

// Order entries by OP Date, then by Order (reference number). Missing values
// sort last in each level.
const sortEntries = (entries: IExportEntryRow[]): IExportEntryRow[] =>
    [...entries].sort((a, b) => {
        if (!a.op_date && b.op_date) return 1
        if (a.op_date && !b.op_date) return -1
        if (a.op_date && b.op_date) {
            const byDate = a.op_date.localeCompare(b.op_date)
            if (byDate !== 0) return byDate
        }
        const ar = a.reference_number, br = b.reference_number
        if (ar == null && br == null) return 0
        if (ar == null) return 1
        if (br == null) return -1
        return ar - br
    })

export default function ExportForm() {
    const { is_edit, export: exportRecord, excluded_order_ids, back_params, errors: serverErrors } =
        usePage<Props>().props
    const { t } = useTranslation()

    const backQueryString = new URLSearchParams(back_params as Record<string, string>).toString()
    const backUrl = `/exports?${backQueryString}`

    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})

    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) setLocalErrors(serverErrors)
    }, [serverErrors])

    const initialValues: IExport = exportRecord
        ? { ...exportRecord, export_entries: sortEntries(exportRecord.export_entries ?? []) }
        : EMPTY_FORM()

    const methods = useForm<IExport>({ defaultValues: initialValues })
    const { control, handleSubmit, reset, getValues } = methods
    const { fields, append, remove, update } = useFieldArray({ control, name: "export_entries" })

    const [pickerOpen, setPickerOpen] = useState(false)

    // Orders already linked in this form (live), so the bulk picker and the
    // per-row linker both hide orders that are already taken here — on top of
    // excluded_order_ids, which covers orders linked from other exports.
    const watchedEntries = methods.watch("export_entries") as IExportEntryRow[] | undefined
    const linkedOrderIds = (watchedEntries ?? [])
        .filter(e => !e._destroy && e.order_id != null)
        .map(e => e.order_id as number)
    const allExcludedOrderIds = [...excluded_order_ids, ...linkedOrderIds]

    const entryFromOrder = (order: IOrderOption): IExportEntryRow => ({
        ...EMPTY_ENTRY(),
        order_id: order.id,
        order: { id: order.id, order_code: order.order_code },
        op_date: order.order_entry_date ?? null,
        reference_number: order.max_reference_number ?? null,
        project_name: order.project_name ?? "",
        paper_color: order.reference_paper_color ?? 0,
        printing_inks_amount: order.printing_inks_amount ?? 0,
        kilograms: order.general_info_programmed_kilograms ?? 0,
    })

    const handleBulkAdd = (orders: IOrderOption[]) => {
        orders.forEach(order => append(entryFromOrder(order)))
        setPickerOpen(false)
    }

    // Mutate the row through useFieldArray.update so the field array's
    // internal snapshot (which getValues uses at submit time) stays in
    // sync — plain setValue on a deep path is unreliable inside useFieldArray.
    const updateEntry = (index: number, patch: Partial<IExportEntryRow>) => {
        const current = methods.getValues(`export_entries.${index}`) as IExportEntryRow
        update(index, { ...current, ...patch })
    }

    // Existing entries (with id) need _destroy: true so Rails actually
    // deletes them via accepts_nested_attributes_for. New rows can just
    // be dropped from the array.
    const removeEntry = (index: number) => {
        const current = methods.getValues(`export_entries.${index}`) as IExportEntryRow
        if (current?.id) {
            update(index, { ...current, _destroy: true })
        } else {
            remove(index)
        }
    }

    useEffect(() => {
        if (exportRecord) {
            reset({ ...exportRecord, export_entries: sortEntries(exportRecord.export_entries ?? []) })
        }
    }, [exportRecord])

    const buildPayload = (data: IExport) => ({
        export: {
            name: data.name,
            client_name: data.client_name,
            update_date: data.update_date,
            export_entries_attributes: data.export_entries.map(({ order, ...rest }) => rest),
        },
    })

    const buildOnError = (originalUrl: string) =>
        (errs: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(Object.fromEntries(
                Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
            ))
        }

    const handleSave: SubmitHandler<IExport> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const url = is_edit
            ? `/exports/${data.id}?${backQueryString}`
            : `/exports?${backQueryString}`
        const method = is_edit ? "patch" : "post"
        router[method](url, buildPayload(data), {
            onError: buildOnError(originalUrl),
            onFinish: () => setSubmitting(false),
        })
    }

    const handleSaveAndContinue: SubmitHandler<IExport> = (data) => {
        setSubmitting(true)
        setLocalErrors({})
        const originalUrl = window.location.pathname + window.location.search
        const payload = { ...buildPayload(data), stay: true }
        if (is_edit) {
            router.patch(`/exports/${data.id}?${backQueryString}`, payload, {
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        } else {
            router.post(`/exports?${backQueryString}`, payload, {
                preserveScroll: true,
                onError: buildOnError(originalUrl),
                onFinish: () => setSubmitting(false),
            })
        }
    }

    const handleDownloadExcel = () => {
        const data = getValues()
        if (!data.id) return
        window.location.href = `/exports/${data.id}/download_excel`
    }

    const title = is_edit
        ? (exportRecord?.name ?? t("export.form.editTitle"))
        : t("export.form.newTitle")

    const headers = [
        t("export.form.entry.opDate"),
        t("export.form.entry.orderNumber"),
        t("export.form.entry.project"),
        t("export.form.entry.paperColor"),
        t("export.form.entry.printingInks"),
        t("export.form.entry.kilograms"),
        t("export.form.entry.artRequest"),
        t("export.form.entry.plateRequest"),
        t("export.form.entry.plateApproval"),
        t("export.form.entry.exportNumber"),
        t("export.form.entry.exitDate"),
        t("export.form.entry.urgent"),
        "",
        "",
    ]

    return (
        <FormProvider {...methods}>
            <Head title={title} />

            <Box sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider" }}>
                <Container maxWidth={false}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <Tooltip title={t("common.back")}>
                            <IconButton LinkComponent={Link} href={backUrl} size="small">
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                        <Typography variant="subtitle1" noWrap
                            sx={{ flexGrow: 1, display: { xs: "none", sm: "block" }, fontWeight: "bold" }}>
                            {title}
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: "block", sm: "none" } }} />
                        {is_edit && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="success"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadExcel}
                                disabled={submitting}
                            >
                                {t("common.excel")}
                            </Button>
                        )}
                        <Button color="inherit" LinkComponent={Link} href={backUrl} size="small" disabled={submitting}>
                            {t("common.cancel")}
                        </Button>
                        <Button variant="outlined" size="small" onClick={handleSubmit(handleSaveAndContinue)} disabled={submitting}>
                            {t("common.saveAndContinue")}
                        </Button>
                        <Button variant="contained" size="small" onClick={handleSubmit(handleSave)} disabled={submitting}>
                            {submitting ? t("common.saving") : t("common.save")}
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 3 } }}>
                {Object.keys(localErrors).length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {formatErrorMessages(t, localErrors).join(" · ")}
                    </Alert>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                    <Controller
                        control={control}
                        name="name"
                        rules={{ required: t("export.form.nameRequired") }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                label={t("export.form.name")}
                                size="small"
                                fullWidth
                                error={!!fieldState.error || !!localErrors.name}
                                helperText={fieldState.error?.message ?? translateErrorMessage(t, localErrors.name)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="client_name"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("export.form.client")}
                                size="small"
                                fullWidth
                                error={!!localErrors.client_name}
                                helperText={translateErrorMessage(t, localErrors.client_name)}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="update_date"
                        rules={{ required: t("export.form.updateDateRequired") }}
                        render={({ field, fieldState }) => (
                            <DatePicker
                                label={t("export.form.updateDate")}
                                value={field.value ? dayjs(field.value) : null}
                                onChange={v => field.onChange(v?.format("YYYY-MM-DD") ?? "")}
                                format="DD-MM-YYYY"
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        fullWidth: true,
                                        error: !!fieldState.error || !!localErrors.update_date,
                                        helperText: fieldState.error?.message
                                            ?? translateErrorMessage(t, localErrors.update_date),
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="overline" color="text.secondary">
                            {t("export.form.entriesSection")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => setPickerOpen(true)}
                            >
                                {t("export.form.addFromOrders")}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => append(EMPTY_ENTRY())}
                            >
                                {t("export.form.addEntry")}
                            </Button>
                        </Box>
                    </Box>

                    {fields.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t("export.form.noEntries")}
                        </Typography>
                    ) : (
                        <Paper variant="outlined" sx={{ mt: 2 }}>
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {headers.map((h, i) => (
                                                <TableCell key={i} sx={{ fontWeight: "bold", whiteSpace: "nowrap", py: 0.75, px: 0.75 }}>
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fields.map((field, index) => {
                                            const isDestroyed = methods.watch(`export_entries.${index}._destroy`)
                                            if (isDestroyed) return null
                                            return (
                                                <EntryRow
                                                    key={field.id}
                                                    index={index}
                                                    parentClientName={methods.watch("client_name") ?? ""}
                                                    excludedOrderIds={allExcludedOrderIds}
                                                    onUpdate={(patch) => updateEntry(index, patch)}
                                                    onRemove={() => removeEntry(index)}
                                                />
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Container>

            <OrderPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onConfirm={handleBulkAdd}
                excludedOrderIds={allExcludedOrderIds}
                multiple
            />
        </FormProvider>
    )
}
